package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.RenderBlock
import dev.lingvo.shared.RenderCellRef
import dev.lingvo.shared.RenderCheckReportDto
import dev.lingvo.shared.ShotCheckSeverity
import dev.lingvo.shared.ShotPushCheckCode
import dev.lingvo.shared.ShotPushCheckDto
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlin.math.floor

/** Presents the server's render blockers before an approval exists; no eligibility is inferred here. */
fun reviewRenderChecks(slug: String, report: RenderCheckReportDto, selected: Set<ReviewSetKey>, slotIds: Map<String, String> = emptyMap()): List<ShotPushCheckDto> = buildList {
    for (check in report.renders) {
        if (ReviewSetKey(check.locale, check.deviceClass) !in selected) continue
        val cell = RenderCellRef(check.slotKey, check.deviceClass, check.locale)
        val path = screenshotsPath(slug, ScreenshotsTab.DESIGN,
            ScreenshotsAreaState(platform = report.platform, slot = slotIds[check.slotKey] ?: check.slotKey, locale = check.locale, device = check.deviceClass))
        for (block in check.blocks) {
            val code = when (block) {
                RenderBlock.CAPTION_NOT_APPROVED -> ShotPushCheckCode.CAPTION_APPROVED
                RenderBlock.CAPTION_CHANGED -> ShotPushCheckCode.CAPTION_UNCHANGED
                RenderBlock.CAPTION_OVERFLOWS -> ShotPushCheckCode.FIT
                RenderBlock.CAPTURE_REPLACED, RenderBlock.CAPTURE_STALE -> ShotPushCheckCode.CAPTURE_CURRENT
                RenderBlock.DESIGN_VERSION_UNAVAILABLE -> ShotPushCheckCode.DESIGN_VERSION_UNAVAILABLE
            }
            val message = when (block) {
                RenderBlock.CAPTION_NOT_APPROVED -> tr("app.projects.slug.screenshots.review_render_checks.the_caption_must_be_approved", "The caption must be approved.")
                RenderBlock.CAPTION_CHANGED -> tr("app.projects.slug.screenshots.review_render_checks.the_caption_changed_after_this_screenshot", "The caption changed after this screenshot was composed. Compose it again.")
                RenderBlock.CAPTION_OVERFLOWS -> tr("app.projects.slug.screenshots.review_render_checks.the_caption_does_not_fit_shorten", "The caption does not fit. Shorten it or widen the text box, then compose it again.")
                RenderBlock.CAPTURE_REPLACED -> tr("app.projects.slug.screenshots.review_render_checks.the_capture_was_replaced_compose_this", "The capture was replaced. Compose this screenshot again.")
                RenderBlock.CAPTURE_STALE -> tr("app.projects.slug.screenshots.review_render_checks.the_capture_is_out_of_date", "The capture is out of date. Upload a current capture, then compose it again.")
                RenderBlock.DESIGN_VERSION_UNAVAILABLE -> unavailableDesignMessage()
            }
            add(ShotPushCheckDto(code, ShotCheckSeverity.RED, message, cell, path))
        }
    }
    for (cell in report.missing) {
        if (ReviewSetKey(cell.locale, cell.deviceClass) in selected) add(ShotPushCheckDto(
            ShotPushCheckCode.RENDER_CURRENT, ShotCheckSeverity.RED,
            tr("app.projects.slug.screenshots.review_render_checks.this_set_has_screenshots_that_still", "This set has screenshots that still need composing."), cell,
            screenshotsPath(slug, ScreenshotsTab.DESIGN, ScreenshotsAreaState(platform = report.platform,
                slot = slotIds[cell.slotKey] ?: cell.slotKey, locale = cell.locale, device = cell.deviceClass)),
        ))
    }
}

/** Stable gate and readability facts are translated here; upstream messages remain text nodes. */
fun readabilityCheckMessage(check: ShotPushCheckDto): String = when (check.code) {
    ShotPushCheckCode.DESIGN_VERSION_UNAVAILABLE -> unavailableDesignMessage()
    ShotPushCheckCode.CAPTION_CONTRAST -> if (check.severity == ShotCheckSeverity.GREEN)
        tr("app.projects.slug.screenshots.review_render_checks.sampled_text_and_local_background_colours", "Sampled text and local background colours meet the contrast target.")
    else tr("app.projects.slug.screenshots.review_render_checks.text_blends_into_its_local_background", "Text blends into its local background. Change the text colour or add an opaque panel.")
    ShotPushCheckCode.THUMBNAIL_LEGIBILITY -> when (check.severity) {
        ShotCheckSeverity.RED -> tr("app.projects.slug.screenshots.review_render_checks.this_caption_is_too_small_to", "This caption is too small to read in a listing thumbnail. Increase its size and compose again.")
        ShotCheckSeverity.AMBER -> tr("app.projects.slug.screenshots.review_render_checks.this_caption_may_be_hard_to", "This caption may be hard to read in a listing thumbnail. Enlarge it and check the preview.")
        ShotCheckSeverity.GREEN -> tr("app.projects.slug.screenshots.review_render_checks.the_caption_clears_the_estimated_thumbnail", "The caption clears the estimated thumbnail reading floor.")
    }
    ShotPushCheckCode.CAPTION_SAFE_AREA -> if (check.severity == ShotCheckSeverity.GREEN)
        tr("app.projects.slug.screenshots.review_render_checks.the_text_box_stays_inside_the", "The text box stays inside the safe area.")
    else tr("app.projects.slug.screenshots.review_render_checks.text_is_clipped_or_close_to", "Text is clipped or close to the safe-area edge. Move it inward or enlarge its text box.")
    ShotPushCheckCode.READABILITY_UNCHECKED -> tr("app.projects.slug.screenshots.review_render_checks.some_text_or_background_details_could", "Some text or background details could not be measured. Check the composed thumbnail visually.")
    else -> check.message
}

private fun unavailableDesignMessage(): String = tr(
    "app.projects.slug.screenshots.review_render_checks.the_design_this_render_was_made",
    "The design this render was made from is no longer stored. Compose it again.",
)

/** Evidence uses downward display rounding so a failing value never appears to clear its target. */
fun readabilityCheckDetails(check: ShotPushCheckDto): String? {
    val evidence = check.readability ?: return null
    fun number(value: Double): String = (floor(value * 100) / 100).toString()
    return when (check.code) {
        ShotPushCheckCode.THUMBNAIL_LEGIBILITY -> {
            val width = evidence.thumbnailWidth?.takeIf { it > 0 } ?: return null
            val height = evidence.estimatedInkHeightPx?.takeIf { it.isFinite() && it >= 0 } ?: return null
            val shownHeight = (floor(height * 10) / 10).toString()
            trf("shots.review.thumbnail_evidence", "At {width}px wide, estimated text height is {height}px. Aim for at least 7px.",
                "width" to width, "height" to shownHeight)
        }
        ShotPushCheckCode.CAPTION_CONTRAST -> {
            val ratio = evidence.contrastRatio?.takeIf { it.isFinite() && it in 1.0..21.0 } ?: return null
            val target = evidence.requiredContrast?.takeIf { it.isFinite() && it in 1.0..21.0 } ?: return null
            trf("shots.review.contrast_evidence", "Sampled contrast is {ratio}:1. The target at this displayed text size is {target}:1.",
                "ratio" to number(ratio), "target" to number(target))
        }
        else -> null
    }
}
